import { ClipboardCheck, Code2, Compass, Wrench } from 'lucide-react';

interface HowItWorksSectionProps {
  id?: string;
}

const steps = [
  {
    icon: Compass,
    title: 'Discovery call',
    description: 'We clarify the systems, data, risks, and success criteria before quoting.',
  },
  {
    icon: ClipboardCheck,
    title: 'Quote',
    description: 'You get a project-specific estimate instead of a vague rate card or hidden agency overhead.',
  },
  {
    icon: Code2,
    title: 'Build & validate',
    description:
      "Dependable integrations, clear data handling, and maintainable code. You don't pay unless you're satisfied.",
  },
  {
    icon: Wrench,
    title: 'Deploy & support',
    description: 'We deploy under your infrastructure or ours, document the system, and help keep it reliable.',
  },
];

export default function HowItWorksSection({ id }: HowItWorksSectionProps) {
  return (
    <section id={id} className="w-full bg-[var(--color-canvas)] py-16 md:py-24 lg:py-28">
      <div className="container px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--color-copper)]">Process</p>
          <h2 className="mt-3 text-3xl font-semibold leading-tight text-[var(--color-ink)] sm:text-5xl">
            Clear scope, transparent communication, reliable deployments
          </h2>
          <p className="mt-5 text-lg leading-8 text-[var(--color-muted)]">
            A small expert shop: direct attention, fair cost, and honest communication instead of a bloated delivery
            machine.
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {steps.map(({ icon: Icon, title, description }, index) => (
            <article key={title} className="rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
              <div className="flex items-center justify-between">
                <div className="grid h-11 w-11 place-items-center rounded-md bg-[var(--color-signal)] text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-semibold text-[var(--color-copper)]">{`0${index + 1}`}</span>
              </div>
              <h3 className="mt-5 text-xl font-semibold text-[var(--color-ink)]">{title}</h3>
              <p className="mt-3 leading-7 text-[var(--color-muted)]">{description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
